"use client"

import { useState, useEffect } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { 
  PlusIcon, 
  ArrowLeft, 
  Loader2, 
  Search, 
  Building2, 
  Users, 
  ChevronRight 
} from "lucide-react"
import { SupplierType, columns } from "./columns"
import { DataTable } from "../../components/data-table"
import SupplierService from '../../api/suppliers.service';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import * as XLSX from 'xlsx';

async function getData(setCanLoadMore: any, skip: number, businessId: number): Promise<SupplierType[]> {
  // Fetch data from API
  const response = await SupplierService.getAllSuppliers(businessId);
  console.log("getAllSuppliers response.data", response.data);
  if (response.data.result === 'invalid') {
    return [];
  }
  // Notify if exactly 50 suppliers are returned
  if (response.data.suppliers.length === 50) {
    setCanLoadMore(true);
  }
  return response.data.suppliers.map((supplier: any) => ({
    id: supplier.id,
    publicId: supplier.publicId,
    name: supplier.name,
    contactPerson: supplier.contactPerson,
    email: supplier.email,
    phoneNumber: supplier.phoneNumber,
    address: supplier.address,
    city: supplier.city,
    state: supplier.state,
    country: supplier.country,
    postalCode: supplier.postalCode,
    taxId: supplier.taxId,
    notes: supplier.notes,
    status: supplier.status,
    timezone: supplier.timezone,
    dateUpdated: supplier.dateUpdated,
    dateCreated: supplier.dateCreated
  }));
}

export default function Suppliers() {
  const [data, setData] = useState<SupplierType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  // Add skip state for pagination
  const [skip, setSkip] = useState(0);
  const [canLoadMore, setCanLoadMore] = useState(false);
  
  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalSuppliers: 0,
    activeSuppliers: 0,
    inactiveSuppliers: 0
  });

  const [searchTerm, setSearchTerm] = useState("");

  const [selectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const businessId = selectedBusiness?.id;
      if (!businessId) return;
      
      const result = await getData(setCanLoadMore, skip, businessId);
      setIsLoading(false);
      setData(result);
      
      // Calculate summary stats
      const total = result.length;
      const active = result.filter(supplier => supplier.status === 'active').length;
      const inactive = result.filter(supplier => supplier.status === 'inactive').length;
        
      setSummaryStats({
        totalSuppliers: total,
        activeSuppliers: active,
        inactiveSuppliers: inactive
      });
    };
    fetchData();
  }, [skip, selectedBusiness]);

  const handleSearch = async () => {
    if (searchTerm.trim() === "") {
      const result = await getData(setCanLoadMore, 0, selectedBusiness?.id);
      setData(result);
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await SupplierService.searchSupplier(selectedBusiness?.id, searchTerm);
      if (response.data.result === 'success') {
        const result = response.data.suppliers.map((supplier: any) => ({
          id: supplier.id,
          publicId: supplier.publicId,
          name: supplier.name,
          contactPerson: supplier.contactPerson,
          email: supplier.email,
          phoneNumber: supplier.phoneNumber,
          address: supplier.address,
          city: supplier.city,
          state: supplier.state,
          country: supplier.country,
          postalCode: supplier.postalCode,
          taxId: supplier.taxId,
          notes: supplier.notes,
          status: supplier.status,
          timezone: supplier.timezone,
          dateUpdated: supplier.dateUpdated,
          dateCreated: supplier.dateCreated
        }));
        setData(result);
        
        // Update stats for filtered data
        const total = result.length;
        const active = result.filter(supplier => supplier.status === 'active').length;
        const inactive = result.filter(supplier => supplier.status === 'inactive').length;
          
        setSummaryStats({
          totalSuppliers: total,
          activeSuppliers: active,
          inactiveSuppliers: inactive
        });
      }
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Suppliers");
    const currentDateTime = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    XLSX.writeFile(workbook, `Suppliers_${currentDateTime}.xlsx`);
  };

  const filterByStatus = (status: string) => {
    setActiveTab(status);
    if (status === "all") {
      const fetchData = async () => {
        const result = await getData(setCanLoadMore, 0, selectedBusiness?.id);
        setData(result);
      };
      fetchData();
      return;
    }
    
    const fetchData = async () => {
      const result = await getData(setCanLoadMore, 0, selectedBusiness?.id);
      const filtered = result.filter(supplier => supplier.status === status);
      setData(filtered);
    };
    fetchData();
  };

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="flex flex-col space-y-8">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900">Suppliers</h1>
            <p className="text-slate-500 mt-1">Manage your business suppliers</p>
          </div>
          <div className="flex space-x-3">
            <Button 
              onClick={() => window.history.back()} 
              variant="outline" 
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <Button 
              onClick={() => window.location.href = '/suppliers/create'} 
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <PlusIcon className="w-4 h-4 mr-2" /> Add Supplier
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Total Suppliers</p>
                  <h3 className="text-2xl font-bold text-slate-900 mt-1">{summaryStats.totalSuppliers}</h3>
                </div>
                <div className="bg-blue-50 p-3 rounded-full">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Active Suppliers</p>
                  <h3 className="text-2xl font-bold text-emerald-600 mt-1">
                    {summaryStats.activeSuppliers}
                  </h3>
                </div>
                <div className="bg-emerald-50 p-3 rounded-full">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500">Inactive Suppliers</p>
                  <h3 className="text-2xl font-bold text-amber-600 mt-1">
                    {summaryStats.inactiveSuppliers}
                  </h3>
                </div>
                <div className="bg-amber-50 p-3 rounded-full">
                  <Users className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search and Filter */}
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-medium text-slate-800">Search & Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[280px]">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search suppliers..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="pl-9 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <Button 
                onClick={handleSearch} 
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
              
              <div className="flex-1 min-w-[280px]">
                <div className="flex flex-wrap gap-2">
                  <Badge 
                    variant={activeTab === "all" ? "default" : "outline"} 
                    className="cursor-pointer px-3 py-1 text-sm"
                    onClick={() => filterByStatus("all")}
                  >
                    All
                  </Badge>
                  <Badge 
                    variant={activeTab === "active" ? "default" : "outline"} 
                    className="cursor-pointer px-3 py-1 text-sm bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-200"
                    onClick={() => filterByStatus("active")}
                  >
                    Active
                  </Badge>
                  <Badge 
                    variant={activeTab === "inactive" ? "default" : "outline"} 
                    className="cursor-pointer px-3 py-1 text-sm bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-200"
                    onClick={() => filterByStatus("inactive")}
                  >
                    Inactive
                  </Badge>
                </div>
              </div>
              
              <Button 
                onClick={exportToExcel} 
                variant="outline" 
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              >
                Export to Excel
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card className="border border-slate-200 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            <DataTable columns={columns} data={data} section="suppliers" isLoading={isLoading} />
            
            {canLoadMore && (
              <div className="flex justify-center p-4 border-t border-slate-200 bg-slate-50">
                <Button 
                  onClick={() => setSkip(skip + 50)} 
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100 flex items-center"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ChevronRight className="w-4 h-4 mr-2" />
                  )}
                  Load More
                  <span className="ml-2 text-xs text-slate-500">
                    Showing {skip + 1} to {skip + data.length}
                  </span>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 